/** Company currency helpers — aligned with apps/api/src/lib/currency.ts and AI currency_utils. */

export const ALLOWED_CURRENCIES = [
  'INR',
  'SGD',
  'USD',
  'AED',
  'MYR',
  'EUR',
  'GBP',
] as const;

export type CurrencyCode = (typeof ALLOWED_CURRENCIES)[number];

export const CURRENCY_META: Record<
  CurrencyCode,
  { symbol: string; locale: string; label: string }
> = {
  INR: { symbol: '₹', locale: 'en-IN', label: 'Indian Rupee' },
  SGD: { symbol: 'S$', locale: 'en-SG', label: 'Singapore Dollar' },
  USD: { symbol: '$', locale: 'en-US', label: 'US Dollar' },
  AED: { symbol: 'AED', locale: 'en-AE', label: 'UAE Dirham' },
  MYR: { symbol: 'RM', locale: 'en-MY', label: 'Malaysian Ringgit' },
  EUR: { symbol: '€', locale: 'de-DE', label: 'Euro' },
  GBP: { symbol: '£', locale: 'en-GB', label: 'British Pound' },
};

export const DEFAULT_CURRENCY: CurrencyCode = 'INR';

export function normalizeCurrency(code?: string | null): CurrencyCode {
  if (!code) return DEFAULT_CURRENCY;
  const upper = code.trim().toUpperCase();
  return (ALLOWED_CURRENCIES as readonly string[]).includes(upper)
    ? (upper as CurrencyCode)
    : DEFAULT_CURRENCY;
}

export function getCurrencySymbol(code?: string | null): string {
  return CURRENCY_META[normalizeCurrency(code)].symbol;
}

export function getCurrencyLocale(code?: string | null): string {
  return CURRENCY_META[normalizeCurrency(code)].locale;
}

export function formatMoney(
  value?: number | string | null,
  currencyCode?: string | null,
): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  const code = normalizeCurrency(currencyCode);
  const { locale } = CURRENCY_META[code];
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${CURRENCY_META[code].symbol}${num.toLocaleString(locale)}`;
  }
}
